import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import iconValidar from '../../assets/icon-validar.svg';

const STATUS_PRESENTATION = {
    VALID: {
        className: 'heading-aprobado',
        icon: 'check_circle',
        messageKey: 'validated.rse.status.valid'
    },
    WARNING: {
        className: 'heading-indeterminado',
        icon: 'warning_amber',
        messageKey: 'validated.rse.status.warning'
    },
    INVALID: {
        className: 'heading-rechazado',
        icon: 'cancel',
        messageKey: 'validated.rse.status.invalid'
    },
    UNSIGNED: {
        className: 'heading-novalido',
        icon: 'info',
        messageKey: 'validated.rse.status.unsigned'
    },
    SERVICE_UNAVAILABLE: {
        className: 'heading-novalido',
        icon: 'info',
        messageKey: 'validated.rse.status.service-unavailable'
    }
};

const DETAIL_STATUSES = ['VALID', 'WARNING', 'INVALID'];
const SEVERITY_INVALID = 'INVALID';
const SEVERITY_WARNING = 'WARNING';

const SIGNATURE_INFO_TARGETS = ['SIGNATURE_STATUS', 'SIGNATURE_INTEGRITY', 'DOCUMENT_INTEGRITY'];
const CERTIFICATE_INFO_TARGETS = ['CERTIFICATE_STATUS', 'TIMESTAMP_STATUS'];

const shouldShowSignatureDetails = (result) => (
    DETAIL_STATUSES.includes(result.status)
    && Array.isArray(result.signatures)
    && result.signatures.length > 0
);

export const getIssueSeverity = (issues, target) => (
    issues?.find((issue) => issue.target === target)?.severity
);

export const getSignatureSeverity = (signature) => {
    if (signature.issues?.some((issue) => issue.severity === SEVERITY_INVALID)) {
        return SEVERITY_INVALID;
    }
    if (signature.issues?.length > 0 || !signature.verify) {
        return SEVERITY_WARNING;
    }
    return null;
};

const getSignatureHeadingClassName = (signature) => {
    const severity = getSignatureSeverity(signature);
    const severityClassName = severity === SEVERITY_INVALID
        ? 'heading-rechazado rse-signature-heading-invalid'
        : severity === SEVERITY_WARNING
            ? 'heading-indeterminado rse-signature-heading-warning'
            : '';

    return `collapse-item-heading rse-signature-heading ${severityClassName}`.trim();
};

export const getIssueRows = (issues, targets, t) => (
    (issues || [])
        .filter((issue) => !targets || targets.includes(issue.target))
        .map((issue) => ({
            label: t(`validated.rse.issue-target.${issue.target}`),
            value: t(`validated.rse.issue-code.${issue.code}`),
            severity: issue.severity
        }))
);

const getSignerCn = (signature) => {
    const cnMatch = signature.cn?.match(/CN=([^,]+)/);
    return cnMatch ? cnMatch[1].trim() : signature.cn;
};

const parseRseDate = (value) => {
    if (!value) {
        return null;
    }

    const [datePart, timePart = '00:00:00'] = value.trim().split(' ');
    const [day, month, year] = datePart.split('/').map(Number);
    const [hours = 0, minutes = 0, seconds = 0] = timePart.split(':').map(Number);

    if (!day || !month || !year) {
        return null;
    }

    return new Date(year, month - 1, day, hours, minutes, seconds);
};

const isExpiredDate = (value) => {
    const date = parseRseDate(value);
    return date instanceof Date && !Number.isNaN(date.getTime()) && date < new Date();
};

const formatValidationDateForFileName = (validationDate) => {
    const dateParts = validationDate?.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);

    if (dateParts) {
        const [, year, month, day, hours, minutes, seconds] = dateParts;
        return `${day}-${month}-${year}_${hours}_${minutes}_${seconds}`;
    }

    const now = new Date();
    const pad = (value) => String(value).padStart(2, '0');
    return `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`;
};

const buildReportFileName = (fileName, validationDate, suffix) => {
    const baseName = (fileName || 'document').replace(/\.pdf$/i, '');
    return `${baseName}_${suffix}_${formatValidationDateForFileName(validationDate)}.pdf`;
};

const base64ToPdfBlob = (base64) => {
    const encodedContent = base64.includes(',') ? base64.split(',').pop() : base64;
    const binaryContent = window.atob(encodedContent);
    const bytes = new Uint8Array(binaryContent.length);

    for (let index = 0; index < binaryContent.length; index += 1) {
        bytes[index] = binaryContent.charCodeAt(index);
    }

    return new Blob([bytes], { type: 'application/pdf' });
};

const DetailTable = ({ rows }) => (
    <div className="component-table-default rse-detail-table">
        <div className="responsive-table">
            <table>
                <tbody>
                    {rows
                        .filter(({ value }) => value !== undefined && value !== null)
                        .map(({ label, value, warning, severity, colSpan }, index) => {
                            if (colSpan) {
                                return (
                                    <tr key={`${label}-${index}`}>
                                        <td colSpan="2">{label}</td>
                                    </tr>
                                );
                            }

                            const rowSeverity = severity || (warning ? SEVERITY_WARNING : null);
                            return (
                                <tr
                                    key={`${label}-${index}`}
                                    className={rowSeverity ? `rse-detail-row-${rowSeverity.toLowerCase()}` : ''}
                                >
                                    <td>{label}</td>
                                    <td>{value}</td>
                                </tr>
                            );
                        })}
                </tbody>
            </table>
        </div>
    </div>
);

export const RseValidatedPage = () => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        if (!state?.result || !state?.file) {
            navigate('/validator', { replace: true });
            return;
        }

        window.collapseContent();
    }, [navigate, state]);

    if (!state?.result || !state?.file) {
        return null;
    }

    const onNavigateClose = () => {
        navigate('/', { replace: true });
    };

    const onNavigateBack = () => {
        navigate('/validator', {
            replace: true,
            state: {
                file: state.file,
                fileBlob: state.fileBlob
            }
        });
    };

    const onDownloadReport = () => {
        if (!state.result.reportPdfBase64 || isDownloading) {
            return;
        }

        setIsDownloading(true);

        try {
            const reportBlob = base64ToPdfBlob(state.result.reportPdfBase64);
            const reportUrl = URL.createObjectURL(reportBlob);
            const reportFileName = buildReportFileName(
                state.file.name,
                state.result.validationDate,
                t('validated.rse.report-file-suffix')
            );
            const downloadLink = document.createElement('a');

            downloadLink.href = reportUrl;
            downloadLink.download = reportFileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            downloadLink.remove();
            URL.revokeObjectURL(reportUrl);
        } finally {
            setIsDownloading(false);
        }
    };

    const statusPresentation = STATUS_PRESENTATION[state.result.status] || STATUS_PRESENTATION.SERVICE_UNAVAILABLE;
    const showSignatureDetails = shouldShowSignatureDetails(state.result);
    const responseIssueRows = getIssueRows(state.result.issues, null, t);
    const showResponseIssues = responseIssueRows.length > 0;
    const shouldStartCollapsed = (state.result.numberOfSignatures || 0) === 0;

    return (
        <main className="main-content main-full">
            <div className="component-main-content-firma">
                <div className="page-container">
                    <div className="card-container">
                        <div className="card-head">
                            <h1 className="card-title">
                                <img src={iconValidar} className="img-fluid" alt={t('validator.title')} />
                                {t('validator.title')}
                            </h1>

                            <div className="head-actions">
                                <button onClick={onNavigateClose} className="btn-small btn-primary-outline">
                                    {t('validated.close')}
                                </button>
                                <button onClick={onNavigateBack} className="btn-small btn-primary">
                                    <span className="material-symbols-outlined"> chevron_backward </span>
                                    {t('validated.back')}
                                </button>
                                <button
                                    onClick={onDownloadReport}
                                    className="btn-small btn-primary"
                                    disabled={!state.result.reportPdfBase64 || isDownloading}
                                >
                                    <span className="material-symbols-outlined">
                                        {isDownloading ? 'progress_activity' : 'download'}
                                    </span>
                                    {t('validated.download')}
                                </button>
                            </div>
                        </div>

                        <div className="card-grid card-full">
                            <div className="component-alert">
                                <div className="alert alert-success-alt">
                                    <span className="material-symbols-outlined"> check_circle </span>
                                    <p>
                                        {t('validated.document-loaded')}
                                        <span>{state.file.name}</span>
                                        <small>
                                            {t('validated.rse.signature-count', {
                                                count: state.result.numberOfSignatures || 0
                                            })}
                                        </small>
                                    </p>
                                </div>
                            </div>

                            <div className="component-collapse-validacion rse-validation-report">
                                <div className="collapse-wrapper">
                                    <h3>{t('validated.validation-report')}</h3>

                                    <div className={`collapse-item ${shouldStartCollapsed ? '' : 'show'}`} tabIndex="0">
                                        <a
                                            href="#rseValidationReport"
                                            className={`collapse-item-heading ${statusPresentation.className}`}
                                            tabIndex="0"
                                            role="button"
                                        >
                                            <span className="material-symbols-outlined">
                                                {statusPresentation.icon}
                                            </span>
                                            {t(statusPresentation.messageKey)}
                                        </a>
                                        <div id="rseValidationReport" className="collapse-item-content">
                                            {showResponseIssues && (
                                                <DetailTable rows={responseIssueRows} />
                                            )}
                                            {showSignatureDetails && (
                                                <div className="rse-signature-list">
                                                    {state.result.signatures.map((signature, index) => (
                                                        <div key={`${signature.serialNumber}-${index}`} className="collapse-item show" tabIndex="0">
                                                            <a
                                                                href={`#rseSignature${index}`}
                                                                className={getSignatureHeadingClassName(signature)}
                                                                tabIndex="0"
                                                                role="button"
                                                            >
                                                                {t('validated.rse.signature-title', { cn: signature.cn })}
                                                            </a>
                                                            <div id={`rseSignature${index}`} className="collapse-item-content">
                                                                <div className="collapse-item show" tabIndex="0">
                                                                    <a href={`#rseSignatureInfo${index}`} className="collapse-item-heading" tabIndex="0" role="button">
                                                                        {t('validated.rse.signature-info')}
                                                                    </a>
                                                                    <div id={`rseSignatureInfo${index}`} className="collapse-item-content">
                                                                        <DetailTable
                                                                            rows={[
                                                                                {
                                                                                    label: t('validated.rse.signing-date'),
                                                                                    value: signature.signingDate
                                                                                },
                                                                                {
                                                                                    label: t('validated.rse.signature-cn'),
                                                                                    value: getSignerCn(signature)
                                                                                }
                                                                            ].concat(getIssueRows(signature.issues, SIGNATURE_INFO_TARGETS, t))}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="collapse-item show" tabIndex="0">
                                                                    <a href={`#rseIssuerInfo${index}`} className="collapse-item-heading" tabIndex="0" role="button">
                                                                        {t('validated.rse.issuer-info')}
                                                                    </a>
                                                                    <div id={`rseIssuerInfo${index}`} className="collapse-item-content">
                                                                        <DetailTable
                                                                            rows={[
                                                                                {
                                                                                    label: t('validated.rse.certificate-issuer'),
                                                                                    value: signature.issuer
                                                                                }
                                                                            ]}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="collapse-item show" tabIndex="0">
                                                                    <a href={`#rseCertificateInfo${index}`} className="collapse-item-heading" tabIndex="0" role="button">
                                                                        {t('validated.rse.certificate-info')}
                                                                    </a>
                                                                    <div id={`rseCertificateInfo${index}`} className="collapse-item-content">
                                                                        <DetailTable
                                                                            rows={[
                                                                                {
                                                                                    label: t('validated.rse.certificate-subject'),
                                                                                    value: signature.subject
                                                                                },
                                                                                {
                                                                                    label: t('validated.rse.certificate-serial-number'),
                                                                                    value: signature.serialNumber
                                                                                }
                                                                            ].concat([
                                                                                {
                                                                                    label: t('validated.rse.certificate-validation-time'),
                                                                                    value: '',
                                                                                    colSpan: true
                                                                                },
                                                                                {
                                                                                    label: t('validated.rse.certificate-valid-from'),
                                                                                    value: signature.certificateValidityFrom
                                                                                },
                                                                                {
                                                                                    label: t('validated.rse.certificate-valid-until'),
                                                                                    value: signature.certificateValidityUntil,
                                                                                    severity: getIssueSeverity(
                                                                                        signature.issues,
                                                                                        'CERTIFICATE_VALIDITY_UNTIL'
                                                                                    ) || (isExpiredDate(signature.certificateValidityUntil)
                                                                                        ? SEVERITY_WARNING
                                                                                        : null)
                                                                                }
                                                                            ]).concat(
                                                                                getIssueRows(
                                                                                    signature.issues,
                                                                                    CERTIFICATE_INFO_TARGETS,
                                                                                    t
                                                                                )
                                                                            )}
                                                                        />
                                                                    </div>

                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};
